import { useCallback, useMemo, useState } from 'react';

import {
  contentTypeLabels,
  formatUpdatedDate,
  getUniqueSpeakers,
  getUniqueStories,
  getUniqueUpdatedBy,
  ITEMS_PER_PAGE,
  matchesClipStatus,
} from './contentItems';

import type {
  ClipStatusFilter,
  ContentItem,
  ContentOwnerFilter,
  ContentType,
} from './contentItems';
import type { DatoSpeaker } from '@src/types.ts/story';

interface RecentContentFilters {
  searchQuery: string;
  setSearchQueryValue: (value: string) => void;
  activeTypeFilters: Set<ContentType>;
  toggleTypeFilter: (type: ContentType) => void;
  activeStoryFilter: string | null;
  setStoryFilter: (storyId: string | null) => void;
  activeUpdatedByFilter: string | null;
  setUpdatedByFilter: (name: string | null) => void;
  /** The selected speaker's display name (speakers are matched by name). */
  activeSpeakerFilter: string | null;
  setSpeakerFilter: (speakerName: string | null) => void;
  activeStatusFilters: Set<ClipStatusFilter>;
  toggleStatusFilter: (status: ClipStatusFilter) => void;
  /**
   * My Content / All Content scope. Derived from `activeUpdatedByFilter`
   * rather than held separately, so the toggle and the Updated By dropdown can
   * never disagree (picking a teammate there rests the toggle on All Content).
   */
  contentOwner: ContentOwnerFilter;
  setContentOwner: (owner: ContentOwnerFilter) => void;
  /** Segment counts, measured after every filter except the owner scope. */
  myContentCount: number;
  allContentCount: number;
  filteredItems: ContentItem[];
  displayedItems: ContentItem[];
  hasMore: boolean;
  remainingCount: number;
  handleViewMore: () => void;
  clearAllFilters: () => void;
  activeFilterCount: number;
  uniqueStories: Array<[string, string]>;
  uniqueUpdatedBy: string[];
  uniqueSpeakers: DatoSpeaker[];
}

export function useRecentContentFilters(
  allContentItems: ContentItem[],
  /** Signed-in user's editor name — the value `updatedBy` is matched against. */
  currentEditorName?: string,
): RecentContentFilters {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTypeFilters, setActiveTypeFilters] = useState<Set<ContentType>>(
    new Set(),
  );
  const [activeStoryFilter, setActiveStoryFilter] = useState<string | null>(
    null,
  );
  // `undefined` = the user hasn't picked a scope yet, so the default is
  // derived below (My Content when they have any, else All Content). Derived
  // rather than set once so it still lands when items / the editor name
  // arrive after the first render.
  const [chosenUpdatedByFilter, setActiveUpdatedByFilter] = useState<
    string | null | undefined
  >(undefined);
  const [activeSpeakerFilter, setActiveSpeakerFilter] = useState<string | null>(
    null,
  );
  const [activeStatusFilters, setActiveStatusFilters] = useState<
    Set<ClipStatusFilter>
  >(new Set());
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);

  const hasOwnContent = useMemo(
    () =>
      !!currentEditorName &&
      allContentItems.some(item => item.updatedBy === currentEditorName),
    [allContentItems, currentEditorName],
  );

  const activeUpdatedByFilter =
    chosenUpdatedByFilter !== undefined
      ? chosenUpdatedByFilter
      : hasOwnContent
        ? (currentEditorName ?? null)
        : null;

  const resetPagination = useCallback(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, []);

  const toggleTypeFilter = useCallback(
    (type: ContentType) => {
      setActiveTypeFilters(prev => {
        const next = new Set(prev);
        if (next.has(type)) {
          next.delete(type);
        } else {
          next.add(type);
        }
        return next;
      });
      resetPagination();
    },
    [resetPagination],
  );

  const setStoryFilter = useCallback(
    (storyId: string | null) => {
      setActiveStoryFilter(storyId);
      resetPagination();
    },
    [resetPagination],
  );

  const setUpdatedByFilter = useCallback(
    (name: string | null) => {
      setActiveUpdatedByFilter(name);
      resetPagination();
    },
    [resetPagination],
  );

  const setSpeakerFilter = useCallback(
    (speakerName: string | null) => {
      setActiveSpeakerFilter(speakerName);
      resetPagination();
    },
    [resetPagination],
  );

  const toggleStatusFilter = useCallback(
    (status: ClipStatusFilter) => {
      setActiveStatusFilters(prev => {
        const next = new Set(prev);
        if (next.has(status)) {
          next.delete(status);
        } else {
          next.add(status);
        }
        return next;
      });
      resetPagination();
    },
    [resetPagination],
  );

  const setSearchQueryValue = useCallback(
    (value: string) => {
      setSearchQuery(value);
      resetPagination();
    },
    [resetPagination],
  );

  const clearAllFilters = useCallback(() => {
    setActiveTypeFilters(new Set());
    setActiveStoryFilter(null);
    setActiveUpdatedByFilter(null);
    setActiveSpeakerFilter(null);
    setActiveStatusFilters(new Set());
    resetPagination();
  }, [resetPagination]);

  // Every filter except Updated By. Split out so the My / All segment counts
  // measure the same slice the owner scope switches between — a count taken
  // after the owner filter would just report the list already on screen.
  const itemsBeforeOwnerFilter = useMemo(() => {
    let items = allContentItems;

    if (activeTypeFilters.size > 0) {
      items = items.filter(item => activeTypeFilters.has(item.contentType));
    }

    if (activeStoryFilter) {
      items = items.filter(item => item.storyId === activeStoryFilter);
    }

    // Speaker filter matches by NAME: the same person is a separate Dato
    // speaker record on each story, so a name match covers all their stories.
    if (activeSpeakerFilter) {
      items = items.filter(item =>
        item.speakers.some(
          speaker => speaker.name?.trim() === activeSpeakerFilter,
        ),
      );
    }

    // Multi-select ORs the checked statuses (matches the Type filter).
    if (activeStatusFilters.size > 0) {
      items = items.filter(item =>
        Array.from(activeStatusFilters).some(status =>
          matchesClipStatus(item, status),
        ),
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => {
        const typeLabel = contentTypeLabels[item.contentType].toLowerCase();
        const dateStr = formatUpdatedDate(item.updatedAt).toLowerCase();
        return (
          item.title.toLowerCase().includes(query) ||
          item.storyTitle.toLowerCase().includes(query) ||
          item.updatedBy.toLowerCase().includes(query) ||
          typeLabel.includes(query) ||
          dateStr.includes(query)
        );
      });
    }

    return items;
  }, [
    allContentItems,
    searchQuery,
    activeTypeFilters,
    activeStoryFilter,
    activeSpeakerFilter,
    activeStatusFilters,
  ]);

  const filteredItems = useMemo(
    () =>
      activeUpdatedByFilter
        ? itemsBeforeOwnerFilter.filter(
            item => item.updatedBy === activeUpdatedByFilter,
          )
        : itemsBeforeOwnerFilter,
    [itemsBeforeOwnerFilter, activeUpdatedByFilter],
  );

  const myContentCount = useMemo(
    () =>
      currentEditorName
        ? itemsBeforeOwnerFilter.filter(
            item => item.updatedBy === currentEditorName,
          ).length
        : 0,
    [itemsBeforeOwnerFilter, currentEditorName],
  );

  // "All Content" is the unscoped slice, so it counts mine too.
  const allContentCount = itemsBeforeOwnerFilter.length;

  const contentOwner: ContentOwnerFilter =
    currentEditorName && activeUpdatedByFilter === currentEditorName
      ? 'mine'
      : 'all';

  const setContentOwner = useCallback(
    (owner: ContentOwnerFilter) => {
      setUpdatedByFilter(owner === 'mine' ? (currentEditorName ?? null) : null);
    },
    [setUpdatedByFilter, currentEditorName],
  );

  const displayedItems = useMemo(
    () => filteredItems.slice(0, displayCount),
    [filteredItems, displayCount],
  );

  const remainingCount = filteredItems.length - displayedItems.length;
  const hasMore = remainingCount > 0;

  const handleViewMore = useCallback(() => {
    setDisplayCount(prev => prev + ITEMS_PER_PAGE);
  }, []);

  const activeFilterCount =
    activeTypeFilters.size +
    (activeStoryFilter ? 1 : 0) +
    (activeUpdatedByFilter ? 1 : 0) +
    (activeSpeakerFilter ? 1 : 0) +
    activeStatusFilters.size;

  const uniqueStories = useMemo(
    () => getUniqueStories(allContentItems),
    [allContentItems],
  );

  const uniqueUpdatedBy = useMemo(
    () => getUniqueUpdatedBy(allContentItems),
    [allContentItems],
  );

  const uniqueSpeakers = useMemo(
    () => getUniqueSpeakers(allContentItems),
    [allContentItems],
  );

  return {
    searchQuery,
    setSearchQueryValue,
    activeTypeFilters,
    toggleTypeFilter,
    activeStoryFilter,
    setStoryFilter,
    activeUpdatedByFilter,
    setUpdatedByFilter,
    activeSpeakerFilter,
    setSpeakerFilter,
    activeStatusFilters,
    toggleStatusFilter,
    contentOwner,
    setContentOwner,
    myContentCount,
    allContentCount,
    filteredItems,
    displayedItems,
    hasMore,
    remainingCount,
    handleViewMore,
    clearAllFilters,
    activeFilterCount,
    uniqueStories,
    uniqueUpdatedBy,
    uniqueSpeakers,
  };
}
